import { useState, useContext, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useLogisticsApi, { transformOrderBoxToEnhanced } from '../../hooks/use-logistics-api';
import { IS_DEBUG } from '../../config/api';
import { useCourierCacheInvalidation } from '../../hooks/use-queries';
import { useProcessedRoutes, setOptimisticStop } from '../../hooks/use-processed-routes';
import { useQueryBridge } from '../../hooks/use-query-bridge';
import useReader from '../../hooks/use-reader';

import Accordion, { AccordionConfig } from "../../components/Shared/Accordion/Accordion";
import { ActionBar, ActionButton } from "../../components/Shared/ActionBar/ActionBar";
import BoxList from "../../components/Shared/BoxList/BoxList";
import Address from "../../components/Shared/Address/Address";
import Signature, { SignatureHandle } from "../../components/Shared/Signature/Signature";
import PhotoCapture from "../../components/PhotoCapture/PhotoCapture";

import AppContext from "../../store/app-context";
import { barcodeShortLabel } from '../../utils/barcode-format';

import './CourierOrder.scss';
import { FaCheck, FaUser, FaPhone, FaClock, FaBox, FaExclamationTriangle, FaWarehouse, FaTruck, FaQrcode, FaList, FaPlus, FaTrash, FaCamera, FaUndo, FaBan, FaUndoAlt, FaArrowLeft } from "react-icons/fa";
import { ProcessedOrder, EnhancedOrderBox, BoxStatusCode, TARGET_STATUS, WorkflowType, parseAddressForDisplay, Address as AddressType } from "../../types/logistics";
import { COMPANY_PHONE, COMPANY_PHONE_TEL } from "../../config/constants";

// Failed collection reasons
const FAILED_COLLECTION_REASONS = [
    { id: 'not_home', label: 'Customer not home' },
    { id: 'refused', label: 'Customer refused collection' },
    { id: 'access_issues', label: 'Cannot access property' },
    { id: 'wrong_address', label: 'Wrong address' },
    { id: 'items_not_ready', label: 'Items not ready' },
    { id: 'other', label: 'Other' },
];

// Failed delivery reasons
const FAILED_DELIVERY_REASONS = [
    { id: 'not_home', label: 'Customer not home' },
    { id: 'refused', label: 'Customer refused delivery' },
    { id: 'access_issues', label: 'Cannot access property' },
    { id: 'wrong_address', label: 'Wrong/incomplete address' },
    { id: 'damaged_package', label: 'Package damaged in transit' },
    { id: 'other', label: 'Other' },
];

// Remove box reasons
const REMOVE_BOX_REASONS = [
    { id: 'not_needed', label: 'Box not needed' },
    { id: 'unsuitable', label: 'Unsuitable for collection' },
    { id: 'wrong_order', label: "Doesn't belong to this order" },
    { id: 'duplicate', label: 'Duplicate box' },
    { id: 'damaged', label: 'Damaged / unusable' },
    { id: 'customer_request', label: 'Customer requested removal' },
    { id: 'other', label: 'Other' },
];

const CourierOrder = () => {
    const appCtx = useContext(AppContext);
    const params = useParams<{ order: string; type: string }>();
    const orderId = Number(params.order);
    const orderType = params.type as 'collection' | 'delivery';

    const navigate = useNavigate();

    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        details: true,
        scan: false,
        photo: false,
        signature: false
    });
    const [manualMode, setManualMode] = useState(false);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [boxToFail, setBoxToFail] = useState<EnhancedOrderBox | null>(null); // null = all boxes, otherwise single box

    // Failed delivery state
    const [showFailedDeliveryModal, setShowFailedDeliveryModal] = useState(false);
    const [selectedDeliveryReason, setSelectedDeliveryReason] = useState<string>('');
    const [boxToFailDelivery, setBoxToFailDelivery] = useState<EnhancedOrderBox | null>(null);

    // Add/remove box state
    const [showAddBoxModal, setShowAddBoxModal] = useState(false);
    const [showRemoveBoxModal, setShowRemoveBoxModal] = useState(false);
    const [boxToRemove, setBoxToRemove] = useState<EnhancedOrderBox | null>(null);
    const [selectedRemoveReason, setSelectedRemoveReason] = useState('');

    // Photo proof state
    const [failedCollectionPhotoBase64, setFailedCollectionPhotoBase64] = useState<string | null>(null);
    const [failedDeliveryPhotoBase64, setFailedDeliveryPhotoBase64] = useState<string | null>(null);
    const [proofPhotoBase64, setProofPhotoBase64] = useState<string | null>(null);

    // Revert failed state
    const [showRevertModal, setShowRevertModal] = useState(false);
    const [boxToRevert, setBoxToRevert] = useState<EnhancedOrderBox | null>(null);
    const [revertTarget, setRevertTarget] = useState<BoxStatusCode>('WCAC');

    const signatureRef = useRef<SignatureHandle>(null);

    // Submission guards — prevent duplicate API calls from double-taps or rapid scans
    const isSubmittingRef = useRef(false);
    const processingBoxIds = useRef(new Set<number>());

    const { startQuagga } = useReader(null);
    const { submitSignature, updateBoxStatus, getStatusCodeForAction, checkCanTransition, addBoxToOrder, removeBoxFromOrder } = useLogisticsApi();
    const { invalidateCourierCache } = useCourierCacheInvalidation();

    // Derive order from shared hook (React Query cache) + URL params
    const { stops, isLoading, error, hasData } = useProcessedRoutes();
    const serverOrder = useMemo(
        () => stops.find(s => s.id === orderId && s.type === orderType) ?? null,
        [stops, orderId, orderType]
    );

    // Local order state for optimistic updates during mutations.
    // Initialized from serverOrder, reset when server data changes (after cache refetch).
    const [localOrder, setLocalOrder] = useState<ProcessedOrder | null>(null);
    useEffect(() => {
        if (serverOrder) {
            setLocalOrder(serverOrder);
        }
    }, [serverOrder]);

    // The displayed order: local optimistic state takes precedence, falls back to server
    const order = localOrder ?? serverOrder;

    // viewOrder replacement — updates local state for optimistic UI
    const viewOrder = useCallback((data: ProcessedOrder | null) => {
        setLocalOrder(data);
    }, []);

    // Write current optimistic state to shared overlay before navigating back to route list.
    // This ensures Courier.tsx sees the updated card state instantly without waiting for refetch.
    const navigateBackWithOptimisticState = useCallback((explicitOrder?: ProcessedOrder) => {
        const current = explicitOrder ?? localOrder ?? serverOrder;
        if (current) {
            setOptimisticStop(current);
        }
        invalidateCourierCache();
        navigate('/courier');
    }, [localOrder, serverOrder, invalidateCourierCache, navigate]);

    // Bridge loading/error to global overlay (only for initial load)
    useQueryBridge(isLoading, error, 'Loading order...', 'Failed to load order', hasData);

    // Extract stable functions from context to avoid dependency on whole appCtx object
    const newLoading = appCtx.newLoading;
    const newMessage = appCtx.newMessage;
    const geolocation = appCtx.geolocation;

    // Get order details for display
    const isCollection = order?.type === 'collection';
    const customerName = order?.customer
        ? `${order.customer.firstName} ${order.customer.lastName}`.trim()
        : 'Unknown Customer';
    const customerPhone = order?.customer?.phone || order?.customer?.phoneNumber;
    const eta = isCollection ? order?.collectionEta : order?.deliveryEta;

    // Existing proof URLs from backend (populated after successful submission + refresh)
    const existingSignatureUrl = isCollection
        ? order?.collectionSignatureUrl
        : order?.deliverySignatureUrl;
    const existingProofPhotoUrl = isCollection
        ? order?.collectionProofPhotoUrl
        : order?.deliveryProofPhotoUrl;

    // Exclude failed-status boxes from the active working set.
    // WCFC (failed collection) boxes are shown in the revert section.
    // ITTKKFR (failed delivery) boxes are excluded from scanning.
    const FAILED_STATUSES: BoxStatusCode[] = useMemo(() => ['WCFC', 'ITTKKFR'], []);
    const activeBoxes = useMemo(
        () => order?.boxes.filter(b => !FAILED_STATUSES.includes(b.statusCode)) ?? [],
        [order?.boxes, FAILED_STATUSES]
    );
    const activeProgress = activeBoxes.filter(b => b.isCompleted).length;
    const failedBoxCount = order?.boxes.filter(b => FAILED_STATUSES.includes(b.statusCode)).length ?? 0;
    const isOrderCompleted = order?.signature || existingSignatureUrl ||
        (isCollection && order?.collectionCompletedAt) ||
        (!isCollection && order?.deliveryCompletedAt);

    // All boxes have reached a terminal state (success or fail) for this phase
    const allBoxesHandled = (activeBoxes.length > 0 && activeProgress === activeBoxes.length)
        || (activeBoxes.length === 0 && failedBoxCount > 0);

    // Every single box is in a failed state (no successful boxes)
    const allBoxesFailed = activeBoxes.length === 0 && failedBoxCount > 0
        && (!order?.pendingWarehouseBoxes || order.pendingWarehouseBoxes === 0);

    const deliveryReadiness = order?.deliveryReadiness;
    const isDeliveryBlocked = !isCollection && deliveryReadiness?.isBlocked === true;

    // Delivery has no boxes to process (e.g. all boxes failed collection → nothing reached delivery pipeline)
    const nothingToDeliver = !isCollection && activeBoxes.length === 0 && failedBoxCount === 0
        && !isDeliveryBlocked && !isOrderCompleted
        && (!order?.pendingWarehouseBoxes || order.pendingWarehouseBoxes === 0);

    // Auto-open the photo section when all boxes are handled (guides courier to next step)
    useEffect(() => {
        if (allBoxesHandled && !allBoxesFailed && !proofPhotoBase64 && !existingProofPhotoUrl && !isOrderCompleted) {
            setOpenSections({ details: false, scan: false, photo: true, signature: false });
        }
    }, [allBoxesHandled, allBoxesFailed, proofPhotoBase64, existingProofPhotoUrl, isOrderCompleted]);

    // Parse address string for display
    const addressString = isCollection ? order?.collectionAddress : order?.deliveryAddress;
    const parsedAddress = parseAddressForDisplay(addressString || null);

    const handleSignatureSubmit = useCallback(async () => {
        if (!order) return;
        if (isSubmittingRef.current) {
            newMessage({ text: 'Submission in progress, please wait...', type: 2 });
            return;
        }

        // Validate signature pad has content
        if (!signatureRef.current || signatureRef.current.isEmpty()) {
            newMessage({
                text: 'Please provide a signature before submitting.',
                type: 0
            });
            return;
        }

        // Block if not all boxes handled
        if (!allBoxesHandled) {
            newMessage({ text: 'All boxes must be handled before submitting.', type: 0 });
            return;
        }

        // Block if photo proof not taken (unless all boxes failed)
        if (!allBoxesFailed && !proofPhotoBase64 && !existingProofPhotoUrl) {
            newMessage({ text: 'Photo proof is required before submitting.', type: 0 });
            return;
        }

        isSubmittingRef.current = true;
        newLoading('Submitting signature...');

        try {

            const isCollection = order.type?.indexOf('collection') !== -1;
            const orderId = order.id;

            const signatureDataUrl = signatureRef.current.getDataUrl() || '';

            await submitSignature(
                orderId,
                signatureDataUrl,
                isCollection,
                proofPhotoBase64 || undefined
            );

            // Update local order state with signature URL so UI switches
            // from canvas to "existing signature" display immediately
            const updatedOrder = structuredClone(order);
            updatedOrder.signature = true;
            if (isCollection) {
                updatedOrder.collectionSignatureUrl = signatureDataUrl;
                updatedOrder.collectionCompletedAt = Date.now();
            } else {
                updatedOrder.deliverySignatureUrl = signatureDataUrl;
                updatedOrder.deliveryCompletedAt = Date.now();
            }
            if (proofPhotoBase64) {
                if (isCollection) {
                    updatedOrder.collectionProofPhotoUrl = proofPhotoBase64;
                } else {
                    updatedOrder.deliveryProofPhotoUrl = proofPhotoBase64;
                }
            }
            viewOrder(updatedOrder);
            setProofPhotoBase64(null);

            newLoading('');
            newMessage({
                text: navigator.onLine
                    ? 'Signature submitted successfully!'
                    : 'Saved offline — will sync when online',
                type: 1
            });

            // Write optimistic state to shared overlay so Courier list updates instantly
            // Pass updatedOrder explicitly — localOrder won't reflect viewOrder() yet (React batches state)
            navigateBackWithOptimisticState(updatedOrder);
        } catch (error: any) {
            console.error('Failed to submit signature:', error);
            isSubmittingRef.current = false;
            newLoading('');

            // Map backend validation errors to user-friendly messages
            const msg = error.message || '';
            let userMessage = 'Failed to submit signature';
            if (msg.includes('BOXES_NOT_COMPLETE')) {
                userMessage = 'All boxes must be processed before submitting signature.';
            } else if (msg.includes('PHOTO_REQUIRED')) {
                userMessage = 'Photo proof is required before submitting.';
            } else if (msg) {
                userMessage = msg;
            }

            newMessage({ text: userMessage, type: 0 });
        }
    }, [order, newLoading, newMessage, viewOrder, submitSignature, navigateBackWithOptimisticState, proofPhotoBase64, allBoxesHandled, allBoxesFailed, existingProofPhotoUrl]);

    // Use a ref to always get the latest order state in async callbacks
    const orderRef = useRef(order);
    orderRef.current = order;

    const updateBox = useCallback(async (box: EnhancedOrderBox | string) => {
        // string value means a location update (only used in warehouse/store, not here)
        if (typeof box === 'string') return;
        const currentOrder = orderRef.current;
        if (!currentOrder) return;

        // Per-box duplicate guard — prevent same box being processed concurrently
        if (processingBoxIds.current.has(box.id)) return;

        const enhancedBox = box;
        const workflowType = (currentOrder.type || 'collection') as WorkflowType;
        const targetStatus = TARGET_STATUS[workflowType];

        // Helper to update a box in order state by ID (not barcode, as barcode can be empty)
        const updateOrderBox = (orderToUpdate: ProcessedOrder, boxId: number, updates: Partial<EnhancedOrderBox>) => {
            orderToUpdate.boxes.forEach((boxInList) => {
                if (boxInList.id === boxId) {
                    Object.assign(boxInList, updates);
                }
            });
            orderToUpdate.progress = orderToUpdate.boxes.filter((b) => b.isCompleted).length;
        };

        // Pre-check: Skip API call if box is already completed
        if (enhancedBox.isCompleted) {
            if (IS_DEBUG) console.log(`Skipping ${box.barcode || box.id}: already completed (${enhancedBox.statusCode})`);
            // Still mark as scanned locally for UI feedback
            const updatedOrder = structuredClone(currentOrder);
            updateOrderBox(updatedOrder, box.id, { scanned: box.scanned });
            viewOrder(updatedOrder);
            orderRef.current = updatedOrder;
            return;
        }

        // Pre-check: Validate transition is possible
        if (enhancedBox.statusCode && targetStatus && !checkCanTransition(enhancedBox.statusCode, targetStatus)) {
            console.warn(`Cannot transition box ${box.barcode || box.id} from ${enhancedBox.statusCode} to ${targetStatus}`);
            // Update local state anyway to show scan feedback
            const updatedOrder = structuredClone(currentOrder);
            updateOrderBox(updatedOrder, box.id, { scanned: box.scanned });
            viewOrder(updatedOrder);
            orderRef.current = updatedOrder;
            return;
        }

        // Update local state immediately for UI responsiveness
        // IMPORTANT: Also update orderRef synchronously so the next rapid scan
        // sees this update immediately (before React re-renders).
        const updatedOrder = structuredClone(currentOrder);
        updateOrderBox(updatedOrder, box.id, { scanned: box.scanned });
        viewOrder(updatedOrder);
        orderRef.current = updatedOrder;

        // If box was scanned, update status on backend
        if (box.scanned) {
            processingBoxIds.current.add(box.id);
            try {
                const isCollection = currentOrder.type?.indexOf('collection') !== -1;
                const statusCode = isCollection ? getStatusCodeForAction('collect') : getStatusCodeForAction('deliver');

                await updateBoxStatus(box.id, statusCode, {
                    latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                    longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined
                });

                // After successful API call, update the box status in local state
                // IMPORTANT: Use orderRef.current to get the LATEST state, not stale closure value
                const latestOrder = orderRef.current;
                if (latestOrder) {
                    const refreshedOrder = structuredClone(latestOrder);
                    updateOrderBox(refreshedOrder, box.id, {
                        scanned: true,
                        statusCode: statusCode,
                        isCompleted: true
                    });
                    viewOrder(refreshedOrder);
                    orderRef.current = refreshedOrder;
                }
                invalidateCourierCache();
            } catch (error: any) {
                console.warn('Failed to update box status on backend:', error);

                // Check if it's an invalid transition error - likely stale data
                const errorMsg = error.message || '';
                if (errorMsg.includes('Invalid transition')) {
                    // Parse the actual status from error message (e.g., "Invalid transition from WC to ITKK")
                    const match = errorMsg.match(/from (\w+) to/);
                    const actualStatus = match?.[1] as BoxStatusCode | undefined;

                    if (actualStatus) {
                        // Update local state with the actual status from backend
                        const latestOrder = orderRef.current;
                        if (latestOrder) {
                            const correctedOrder = structuredClone(latestOrder);
                            // If actual status is WC (delivered) or past the target, mark as completed
                            const isActuallyComplete = actualStatus === 'WC' ||
                                actualStatus === 'ITKK' || actualStatus === 'ITTC';
                            updateOrderBox(correctedOrder, box.id, {
                                statusCode: actualStatus,
                                isCompleted: isActuallyComplete,
                                scanned: isActuallyComplete
                            });
                            viewOrder(correctedOrder);
                            orderRef.current = correctedOrder;
                        }
                        invalidateCourierCache();

                        newMessage({
                            text: `Box status is ${actualStatus} - data refreshed`,
                            type: 2 // warning
                        });
                    }
                } else {
                    // Non-transition error (network, server, etc.) — show user feedback
                    newMessage({
                        text: `Failed to update box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)}: ${errorMsg || 'Unknown error'}`,
                        type: 0
                    });
                }
            } finally {
                processingBoxIds.current.delete(box.id);
            }
        }
    }, [viewOrder, geolocation, updateBoxStatus, getStatusCodeForAction, checkCanTransition, newMessage, invalidateCourierCache]);

    // Handle failed collection - updates single box or all uncollected boxes to WCFC
    const handleFailedCollection = useCallback(async (reason: string, singleBox?: EnhancedOrderBox | null, photoBase64?: string | null) => {
        if (!order) return;
        if (isSubmittingRef.current) {
            newMessage({ text: 'Please wait, a submission is already in progress.', type: 2 });
            return;
        }
        if (order.signature || order.collectionCompletedAt) {
            newMessage({ text: 'This collection has already been completed.', type: 2 });
            return;
        }
        
        // Only for collection orders
        const isCollection = order.type?.indexOf('collection') !== -1;
        if (!isCollection) {
            newMessage({
                text: 'Failed collection is only available for collection orders',
                type: 0
            });
            return;
        }

        const isSingleBox = singleBox !== null && singleBox !== undefined;
        isSubmittingRef.current = true;
        newLoading(isSingleBox ? 'Marking box as failed...' : 'Marking collection as failed...');

        try {
            // Get boxes to update - either single box or all uncollected boxes
            const boxesToFail = isSingleBox
                ? [singleBox]
                : order.boxes.filter((box: EnhancedOrderBox) => 
                    box.statusCode === 'WCAC' && !box.scanned && !box.isCompleted
                );

            if (boxesToFail.length === 0) {
                isSubmittingRef.current = false;
                newLoading('');
                newMessage({
                    text: 'No uncollected boxes to mark as failed',
                    type: 2
                });
                return;
            }

            const failedStatusCode = getStatusCodeForAction('fail-collection'); // Returns 'WCFC'

            // Update each box to WCFC
            const updatePromises = boxesToFail.map((box: EnhancedOrderBox) =>
                updateBoxStatus(box.id, failedStatusCode, {
                    latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                    longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined,
                    notes: `Failed collection: ${reason}`,
                    photoBase64: photoBase64 || undefined
                })
            );

            await Promise.all(updatePromises);
            setFailedCollectionPhotoBase64(null);

            // Update local state
            const updatedOrder = structuredClone(order);
            const failedBoxIds = boxesToFail.map(b => b.id);
            updatedOrder.boxes.forEach((boxInList: EnhancedOrderBox) => {
                if (failedBoxIds.includes(boxInList.id)) {
                    boxInList.statusCode = 'WCFC';
                    boxInList.isCompleted = true; // Mark as completed (terminal state)
                }
            });
            // Recalculate progress
            updatedOrder.progress = updatedOrder.boxes.filter((b: EnhancedOrderBox) => b.isCompleted).length;
            viewOrder(updatedOrder);

            // Single-box fail: invalidate locally so list updates on next view
            // Bulk fail: let Courier.tsx handle invalidation via refresh state
            if (isSingleBox) {
                invalidateCourierCache();
            }

            newLoading('');
            newMessage({
                text: isSingleBox
                    ? `Box ${barcodeShortLabel(singleBox.barcode, singleBox.id, singleBox.barcodeComponents)} marked as failed: ${reason}`
                    : `Collection marked as failed: ${reason}`,
                type: 1
            });

            // Only navigate back for bulk fail (all boxes), never for single-box fail
            if (!isSingleBox) {
                navigateBackWithOptimisticState(updatedOrder);
            }
        } catch (error: any) {
            console.error('Failed to mark collection as failed:', error);
            newLoading('');
            newMessage({
                text: error.message || 'Failed to update collection status',
                type: 0
            });
        } finally {
            isSubmittingRef.current = false;
        }
    }, [order, newLoading, newMessage, viewOrder, updateBoxStatus, getStatusCodeForAction, geolocation, navigateBackWithOptimisticState, invalidateCourierCache]);

    // Callback for marking a single box as failed (passed to BoxList)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleFailSingleBox = useCallback((box: EnhancedOrderBox) => {
        // Only allow for uncollected boxes in collection orders
        if (box.statusCode !== 'WCAC' || box.scanned || box.isCompleted) {
            newMessage({
                text: 'This box cannot be marked as failed',
                type: 2
            });
            return;
        }
        setBoxToFail(box);
        setShowFailedModal(true);
    }, [newMessage]);

    // Handle failed delivery - updates single box or all undelivered boxes to ITTKKFR
    const handleFailedDelivery = useCallback(async (reason: string, singleBox?: EnhancedOrderBox | null, photoBase64?: string | null) => {
        if (!order) return;
        if (isSubmittingRef.current) {
            newMessage({ text: 'Please wait, a submission is already in progress.', type: 2 });
            return;
        }
        if (order.signature || order.deliveryCompletedAt) {
            newMessage({ text: 'This delivery has already been completed.', type: 2 });
            return;
        }

        // Only for delivery orders
        const isDelivery = order.type?.indexOf('delivery') !== -1 && order.type?.indexOf('collection') === -1;
        if (!isDelivery) {
            newMessage({
                text: 'Failed delivery is only available for delivery orders',
                type: 0
            });
            return;
        }

        const isSingleBox = singleBox !== null && singleBox !== undefined;
        isSubmittingRef.current = true;
        newLoading(isSingleBox ? 'Marking box as failed...' : 'Marking delivery as failed...');

        try {
            // Get boxes to update - either single box or all undelivered ITTC boxes
            const boxesToFail = isSingleBox
                ? [singleBox]
                : order.boxes.filter((box: EnhancedOrderBox) =>
                    box.statusCode === 'ITTC' && !box.scanned && !box.isCompleted
                );

            if (boxesToFail.length === 0) {
                isSubmittingRef.current = false;
                newLoading('');
                newMessage({
                    text: 'No undelivered boxes to mark as failed',
                    type: 2
                });
                return;
            }

            const failedStatusCode = getStatusCodeForAction('fail-delivery'); // Returns 'ITTKKFR'

            // Update each box to ITTKKFR
            const updatePromises = boxesToFail.map((box: EnhancedOrderBox) =>
                updateBoxStatus(box.id, failedStatusCode, {
                    latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                    longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined,
                    notes: `Failed delivery: ${reason}`,
                    photoBase64: photoBase64 || undefined
                })
            );

            await Promise.all(updatePromises);
            setFailedDeliveryPhotoBase64(null);

            // Update local state
            const updatedOrder = structuredClone(order);
            const failedBoxIds = boxesToFail.map(b => b.id);
            updatedOrder.boxes.forEach((boxInList: EnhancedOrderBox) => {
                if (failedBoxIds.includes(boxInList.id)) {
                    boxInList.statusCode = 'ITTKKFR';
                    boxInList.isCompleted = true; // Mark as completed (terminal for this delivery attempt)
                }
            });
            // Recalculate progress
            updatedOrder.progress = updatedOrder.boxes.filter((b: EnhancedOrderBox) => b.isCompleted).length;
            viewOrder(updatedOrder);

            if (isSingleBox) {
                invalidateCourierCache();
            }

            newLoading('');
            newMessage({
                text: isSingleBox
                    ? `Box ${barcodeShortLabel(singleBox.barcode, singleBox.id, singleBox.barcodeComponents)} marked as failed: ${reason}`
                    : `Delivery marked as failed: ${reason}`,
                type: 1
            });

            // Only navigate back for bulk fail (all boxes), never for single-box fail
            if (!isSingleBox) {
                navigateBackWithOptimisticState(updatedOrder);
            }
        } catch (error: any) {
            console.error('Failed to mark delivery as failed:', error);
            newLoading('');
            newMessage({
                text: error.message || 'Failed to update delivery status',
                type: 0
            });
        } finally {
            isSubmittingRef.current = false;
        }
    }, [order, newLoading, newMessage, viewOrder, updateBoxStatus, getStatusCodeForAction, geolocation, navigateBackWithOptimisticState, invalidateCourierCache]);

    // Revert a failed collection status back to awaiting collection: WCFC → WCAC
    const handleRevertFailed = useCallback(async (box: EnhancedOrderBox, targetStatus: BoxStatusCode) => {
        if (!order) return;
        newLoading('Reverting...');
        try {
            await updateBoxStatus(box.id, targetStatus, {
                latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined,
                notes: `Reverted from ${box.statusCode}`
            });

            const updatedOrder = structuredClone(order);
            updatedOrder.boxes.forEach((boxInList: EnhancedOrderBox) => {
                if (boxInList.id === box.id) {
                    boxInList.statusCode = targetStatus;
                    boxInList.isCompleted = false;
                    boxInList.isActionable = true;
                    delete boxInList.scanned;
                }
            });
            updatedOrder.progress = updatedOrder.boxes.filter((b: EnhancedOrderBox) => b.isCompleted).length;
            viewOrder(updatedOrder);
            invalidateCourierCache();

            newLoading('');
            const targetLabel = targetStatus === 'ITTC' ? 'out for delivery' : 'awaiting collection';
            newMessage({
                text: `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} reverted to ${targetLabel}`,
                type: 1
            });
            setShowRevertModal(false);
            setBoxToRevert(null);
        } catch (error: any) {
            newLoading('');
            newMessage({ text: error.message || 'Failed to revert', type: 0 });
        }
    }, [order, updateBoxStatus, geolocation, viewOrder, newLoading, newMessage, invalidateCourierCache]);

    // Handle adding a box to the order
    const handleAddBox = useCallback(async () => {
        if (!order) return;

        newLoading('Adding box...');

        try {
            const newOrderBox = await addBoxToOrder(order.id, {
                latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined
            });

            // Transform to enhanced box and set workflow flags
            const enhanced = transformOrderBoxToEnhanced(newOrderBox);
            enhanced.isActionable = true;
            enhanced.isCompleted = false;
            enhanced.isNewlyAdded = true;

            // Append to local order state
            const updatedOrder = structuredClone(order);
            updatedOrder.boxes.push(enhanced);
            viewOrder(updatedOrder);
            invalidateCourierCache();

            newLoading('');
            newMessage({
                text: navigator.onLine
                    ? `Box added: ${newOrderBox.barcode || newOrderBox.id}`
                    : `Box added offline — will sync when online`,
                type: 1
            });
        } catch (error: any) {
            console.error('Failed to add box:', error);
            newLoading('');
            newMessage({
                text: error.message || 'Failed to add box',
                type: 0
            });
        }
    }, [order, addBoxToOrder, geolocation, viewOrder, newLoading, newMessage, invalidateCourierCache]);

    // Handle removing a box from the order
    const handleRemoveBox = useCallback(async (box: EnhancedOrderBox, reason?: string) => {
        if (!order) return;

        newLoading('Removing box...');

        try {
            await removeBoxFromOrder(order.id, box.id, {
                reason: reason || undefined,
                latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined
            });

            // Remove from local state
            const updatedOrder = structuredClone(order);
            updatedOrder.boxes = updatedOrder.boxes.filter((b: EnhancedOrderBox) => b.id !== box.id);
            updatedOrder.progress = updatedOrder.boxes.filter((b: EnhancedOrderBox) => b.isCompleted).length;
            viewOrder(updatedOrder);
            invalidateCourierCache();

            newLoading('');
            newMessage({
                text: navigator.onLine
                    ? `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} removed`
                    : `Box ${barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} removed offline — will sync when online`,
                type: 1
            });
        } catch (error: any) {
            console.error('Failed to remove box:', error);
            newLoading('');
            newMessage({
                text: error.message || 'Failed to remove box',
                type: 0
            });
        }
    }, [order, removeBoxFromOrder, geolocation, viewOrder, newLoading, newMessage, invalidateCourierCache]);

    // Handlers for inline box actions in the scan BoxList
    const handleFailBoxFromList = useCallback((box: EnhancedOrderBox) => {
        if (isCollection) {
            if (box.statusCode !== 'WCAC' || box.scanned || box.isCompleted) {
                newMessage({ text: 'This box cannot be marked as failed', type: 2 });
                return;
            }
            setBoxToFail(box);
            setShowFailedModal(true);
        } else {
            if (box.statusCode !== 'ITTC' || box.scanned || box.isCompleted) {
                newMessage({ text: 'This box cannot be marked as failed', type: 2 });
                return;
            }
            setBoxToFailDelivery(box);
            setShowFailedDeliveryModal(true);
        }
    }, [isCollection, newMessage]);

    const handleRemoveBoxFromList = useCallback((box: EnhancedOrderBox) => {
        setBoxToRemove(box);
        setShowRemoveBoxModal(true);
    }, []);

    // Determine scan section status indicator
    const getScanHeadingExtra = () => {
        if (!order) return null;

        // If all active boxes complete, show checkmark
        if (activeBoxes.length > 0 && activeProgress === activeBoxes.length) {
            return <FaCheck className="ok" />;
        }

        // If no active boxes and all are failed, show failed text
        if (activeBoxes.length === 0 && failedBoxCount > 0 &&
            (!order.pendingWarehouseBoxes || order.pendingWarehouseBoxes === 0)) {
            return <span className="failed-text">all failed</span>;
        }

        // Nothing to deliver (all boxes failed collection — no delivery-eligible boxes)
        if (nothingToDeliver) {
            return <span className="failed-text">n/a</span>;
        }

        // If blocked delivery, show not ready
        if (activeBoxes.length === 0 && isDeliveryBlocked) {
            return <span className="waiting">not ready</span>;
        }

        // If no active boxes but pending warehouse boxes, show warning
        if (activeBoxes.length === 0 && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0) {
            return <span className="waiting">awaiting warehouse</span>;
        }

        // Normal progress indicator
        return <span>{activeProgress + ' of ' + activeBoxes.length}</span>;
    };

    // Determine photo proof section status indicator
    const getPhotoHeadingExtra = () => {
        if (!order) return null;

        // Photo already taken or submitted
        if (proofPhotoBase64 || existingProofPhotoUrl) {
            return <span className="section-badge-done"><FaCheck /> Taken</span>;
        }

        // All boxes failed — photo not needed
        if (allBoxesFailed) {
            return <span className="failed-text">not applicable</span>;
        }

        // Nothing to deliver (all failed collection)
        if (nothingToDeliver) {
            return <span className="failed-text">not applicable</span>;
        }

        // Order already completed
        if (isOrderCompleted) return null;

        // Delivery blocked
        if (isDeliveryBlocked) {
            return <span className="waiting">not available</span>;
        }

        // Pending warehouse boxes
        if (activeBoxes.length === 0 && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0) {
            return <span className="waiting">not available</span>;
        }

        // Not all boxes handled — show progress
        if (!allBoxesHandled) {
            return <span className="waiting">{activeProgress}/{activeBoxes.length} boxes</span>;
        }

        // All handled, no photo yet
        return <span className="waiting">required</span>;
    };

    // Determine signature section status indicator
    const getSignatureHeadingExtra = () => {
        if (!order) return null;

        // If signed (local flag or existing URL from backend), show checkmark
        if (order.signature || existingSignatureUrl) {
            return <FaCheck className="ok" />;
        }

        // If no active boxes and all are failed, signature not applicable
        if (allBoxesFailed) {
            return <span className="failed-text">not applicable</span>;
        }

        // Nothing to deliver (all failed collection)
        if (nothingToDeliver) {
            return <span className="failed-text">not applicable</span>;
        }

        // If blocked delivery, show not available
        if (isDeliveryBlocked) {
            return <span className="waiting">not available</span>;
        }

        // If no active boxes and pending warehouse boxes, show not available
        if (activeBoxes.length === 0 && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0) {
            return <span className="waiting">not available</span>;
        }

        // Not all boxes handled yet — show progress
        if (!allBoxesHandled) {
            return <span className="waiting">{activeProgress}/{activeBoxes.length} boxes</span>;
        }

        // All handled but photo not taken yet
        if (!allBoxesFailed && !proofPhotoBase64 && !existingProofPhotoUrl) {
            return <span className="waiting">needs photo</span>;
        }

        // Normal state — ready to sign
        return <span>not signed yet</span>;
    };

    const sections: Record<string, AccordionConfig> = {
        details: {
            heading: 'Details',
            name: 'details',
            content: ''
        },
        scan: {
            heading: 'Scan boxes',
            headingExtra: getScanHeadingExtra(),
            name: 'scan',
            content: <>
                {/* Loading barrier — WKKHO boxes must be loaded at warehouse before delivery */}
                {order && order.type?.includes('delivery') && order.boxes.some((b: EnhancedOrderBox) => b.statusCode === 'WKKHO') && (
                    <div className="load-required-notice">
                        <FaTruck className="notice-icon" />
                        <div className="notice-text">
                            <strong>{order.boxes.filter((b: EnhancedOrderBox) => b.statusCode === 'WKKHO').length} box(es) need loading first</strong>
                            <div className="loading-steps">
                                <span className="step"><strong>Step 1:</strong> Load boxes at warehouse</span>
                                <span className="step-arrow">&#8594;</span>
                                <span className="step"><strong>Step 2:</strong> Deliver to customer</span>
                            </div>
                            <p className="notice-detail">These boxes are waiting at the warehouse. They cannot be scanned for delivery until loaded.</p>
                            <button className="btn-go-load" onClick={() => navigate('/courier/warehouse/load')}>
                                <FaTruck /> Go to Warehouse Load
                            </button>
                        </div>
                    </div>
                )}
                {order && activeBoxes.length > 0 && <BoxList
                    boxes={activeBoxes}
                    progress={activeProgress}
                    update={updateBox}
                    manualMode={manualMode}
                    orderType={order.type}
                    onFailBox={!isOrderCompleted ? handleFailBoxFromList : undefined}
                    onRemoveBox={order.type === 'collection' && !isOrderCompleted ? handleRemoveBoxFromList : undefined}
                />}
                {order && activeBoxes.length === 0 && isDeliveryBlocked && deliveryReadiness?.bannerMessage && (
                    <div className="pending-warehouse-notice">
                        <FaBan className="notice-icon" />
                        <div className="notice-text">
                            <strong>No boxes ready for delivery</strong>
                            <p>{deliveryReadiness.bannerMessage}</p>
                            {deliveryReadiness.bannerDetail && (
                                <p>{deliveryReadiness.bannerDetail}</p>
                            )}
                        </div>
                    </div>
                )}
                {order && activeBoxes.length === 0 && !isDeliveryBlocked && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0 && (
                    <div className="pending-warehouse-notice">
                        <FaWarehouse className="notice-icon" />
                        <div className="notice-text">
                            <strong>{order.pendingWarehouseBoxes} box{order.pendingWarehouseBoxes > 1 ? 'es' : ''} awaiting warehouse preparation</strong>
                            <p>These boxes will appear here once the warehouse has prepared them for delivery.</p>
                        </div>
                    </div>
                )}
                {order && activeBoxes.length === 0 && failedBoxCount > 0 &&
                 (!order.pendingWarehouseBoxes || order.pendingWarehouseBoxes === 0) && (
                    <div className="all-failed-notice">
                        <FaExclamationTriangle className="notice-icon" />
                        <div className="notice-text">
                            <strong>All boxes marked as failed</strong>
                            <p>You can revert failed boxes from the Details section to retry.</p>
                        </div>
                    </div>
                )}
                {order && nothingToDeliver && (
                    <div className="all-failed-notice">
                        <FaExclamationTriangle className="notice-icon" />
                        <div className="notice-text">
                            <strong>Nothing to deliver</strong>
                            <p>No boxes reached the delivery stage for this order. This may be because all boxes failed collection.</p>
                        </div>
                    </div>
                )}
            </>
        },
        photo: {
            heading: 'Photo Proof',
            headingExtra: getPhotoHeadingExtra(),
            name: 'photo',
            content: (
                <div className="photo-proof-section">
                    {existingProofPhotoUrl && !proofPhotoBase64 ? (
                        <>
                            <p className="photo-proof-hint">Photo submitted for this {isCollection ? 'collection' : 'delivery'}.</p>
                            <div className="photo-proof-preview">
                                <img src={existingProofPhotoUrl} alt="Proof of condition" />
                            </div>
                        </>
                    ) : isDeliveryBlocked ? (
                        <div className="pending-warehouse-notice">
                            <FaBan className="notice-icon" />
                            <div className="notice-text">
                                <strong>Photo not available</strong>
                                <p>This order is not ready for delivery. Photo can be taken once boxes are loaded.</p>
                            </div>
                        </div>
                    ) : allBoxesFailed ? (
                        <div className="all-failed-notice">
                            <FaExclamationTriangle className="notice-icon" />
                            <div className="notice-text">
                                <strong>Photo proof not required</strong>
                                <p>All boxes have been marked as failed. You can revert failed boxes from the Details section to retry.</p>
                            </div>
                        </div>
                    ) : nothingToDeliver ? (
                        <div className="all-failed-notice">
                            <FaExclamationTriangle className="notice-icon" />
                            <div className="notice-text">
                                <strong>Photo proof not required</strong>
                                <p>No boxes reached the delivery stage for this order.</p>
                            </div>
                        </div>
                    ) : order && activeBoxes.length === 0 && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0 ? (
                        <div className="pending-warehouse-notice">
                            <FaWarehouse className="notice-icon" />
                            <div className="notice-text">
                                <strong>Photo not available</strong>
                                <p>All boxes are still awaiting warehouse preparation. Photo can be taken once boxes are ready.</p>
                            </div>
                        </div>
                    ) : !allBoxesHandled ? (
                        <div className="pending-warehouse-notice">
                            <FaCamera className="notice-icon" />
                            <div className="notice-text">
                                <strong>Handle all boxes first</strong>
                                <p>Photo proof will be available once all boxes have been scanned. Progress: {activeProgress} of {activeBoxes.length} boxes handled.</p>
                            </div>
                        </div>
                    ) : proofPhotoBase64 ? (
                        <>
                            <p className="photo-proof-hint">Photo of boxes as proof of {isCollection ? 'collection' : 'delivery'}.</p>
                            <div className="photo-proof-preview">
                                <img src={proofPhotoBase64} alt="Proof of condition" />
                                <button className="btn-retake-proof" onClick={() => setProofPhotoBase64(null)}>Retake</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="photo-proof-hint">Take a photo of the boxes as proof of {isCollection ? 'collection' : 'delivery'}.</p>
                            <PhotoCapture
                                onCapture={(base64) => {
                                    setProofPhotoBase64(base64);
                                    toggleSection('signature');
                                }}
                                label={`Photo of boxes`}
                            />
                        </>
                    )}
                </div>
            )
        },
        signature: {
            heading: 'Signature',
            headingExtra: getSignatureHeadingExtra(),
            name: 'signature',
            content: order && allBoxesFailed && !isDeliveryBlocked ? (
                <div className="all-failed-notice">
                    <FaExclamationTriangle className="notice-icon" />
                    <div className="notice-text">
                        <strong>Signature not required</strong>
                        <p>All boxes have been marked as failed. You can revert failed boxes from the Details section to retry.</p>
                    </div>
                </div>
            ) : order && nothingToDeliver ? (
                <div className="all-failed-notice">
                    <FaExclamationTriangle className="notice-icon" />
                    <div className="notice-text">
                        <strong>Signature not required</strong>
                        <p>No boxes reached the delivery stage for this order.</p>
                    </div>
                </div>
            ) : order && isDeliveryBlocked ? (
                <div className="pending-warehouse-notice">
                    <FaBan className="notice-icon" />
                    <div className="notice-text">
                        <strong>Signature not available</strong>
                        <p>This order is not ready for delivery. Signature can be collected once boxes are loaded.</p>
                    </div>
                </div>
            ) : order && activeBoxes.length === 0 && order.pendingWarehouseBoxes && order.pendingWarehouseBoxes > 0 ? (
                <div className="pending-warehouse-notice">
                    <FaWarehouse className="notice-icon" />
                    <div className="notice-text">
                        <strong>Signature not available</strong>
                        <p>All boxes are still awaiting warehouse preparation. Signature can be collected once boxes are ready for delivery.</p>
                    </div>
                </div>
            ) : order && !allBoxesHandled ? (
                <div className="pending-warehouse-notice">
                    <FaCamera className="notice-icon" />
                    <div className="notice-text">
                        <strong>Complete all box scans first</strong>
                        <p>Signature will be available once all boxes have been handled. Progress: {activeProgress} of {activeBoxes.length} boxes.</p>
                    </div>
                </div>
            ) : order && !allBoxesFailed && !proofPhotoBase64 && !existingProofPhotoUrl ? (
                <div className="pending-warehouse-notice">
                    <FaCamera className="notice-icon" />
                    <div className="notice-text">
                        <strong>Photo proof required</strong>
                        <p>Please take a photo in the Photo Proof section before signing.</p>
                    </div>
                </div>
            ) : existingSignatureUrl ? (
                <div className="existing-signature">
                    <p className="signature-submitted-hint">Signature submitted for this {isCollection ? 'collection' : 'delivery'}.</p>
                    <img
                        src={existingSignatureUrl}
                        alt="Submitted signature"
                        className="signature-image"
                        onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const fallback = document.createElement('p');
                            fallback.className = 'signature-load-error';
                            fallback.textContent = 'Signature on file (image unavailable)';
                            target.parentElement?.appendChild(fallback);
                        }}
                    />
                </div>
            ) : <Signature ref={signatureRef} />
        }
    };

    if (order) {
        // Convert parsed address to AddressType format for Address component
        const addressData: AddressType | null = parsedAddress ? {
            address1: parsedAddress.address1,
            city: parsedAddress.city,
            postcode: parsedAddress.postcode,
        } : null;
        sections.details.content = (
            <div className="order-details-content">
                <div className="detail-row customer-row">
                    <FaUser className="detail-icon" />
                    <div className="detail-info">
                        <span className="detail-label">Customer</span>
                        <span className="detail-value">{customerName}</span>
                    </div>
                </div>
                {customerPhone ? (
                    <div className="detail-row phone-row">
                        <FaPhone className="detail-icon" />
                        <div className="detail-info">
                            <span className="detail-label">Phone</span>
                            <a href={`tel:${customerPhone}`} className="detail-value phone-link">{customerPhone}</a>
                        </div>
                    </div>
                ) : (
                    <div className="detail-row phone-row fallback-phone">
                        <FaPhone className="detail-icon" />
                        <div className="detail-info">
                            <span className="detail-label">Office</span>
                            <a href={`tel:${COMPANY_PHONE_TEL}`} className="detail-value phone-link">{COMPANY_PHONE}</a>
                            <span className="fallback-hint">Customer phone not available — call office for assistance</span>
                        </div>
                    </div>
                )}
                {eta && (
                    <div className="detail-row eta-row">
                        <FaClock className="detail-icon" />
                        <div className="detail-info">
                            <span className="detail-label">Time Slot</span>
                            <span className="detail-value">{eta}</span>
                        </div>
                    </div>
                )}
                <div className="detail-row box-row">
                    <FaBox className="detail-icon" />
                    <div className="detail-info">
                        <span className="detail-label">Boxes</span>
                        <span className="detail-value">
                            {isDeliveryBlocked && order.allBoxCount
                                ? `${order.allBoxCount} boxes (not ready for delivery)`
                                : `${activeBoxes.length} boxes to ${isCollection ? 'collect' : 'deliver'}`
                            }
                        </span>
                    </div>
                </div>
                {isDeliveryBlocked && deliveryReadiness && (
                    <div className="detail-row box-breakdown-row">
                        <FaWarehouse className="detail-icon" />
                        <div className="detail-info">
                            <span className="detail-label">Box Status Breakdown</span>
                            <span className="detail-value breakdown">{deliveryReadiness.counts.total > 0 && deliveryReadiness.bannerDetail}</span>
                        </div>
                    </div>
                )}
                {/* Add/Remove/Fail box controls - collection orders only */}
                {order.type === 'collection' && !order.signature && (
                    <div className="box-management-section">
                        <button
                            className="btn-add-box"
                            onClick={() => setShowAddBoxModal(true)}
                        >
                            <FaPlus />
                            Add Box
                        </button>
                        {order.boxes.filter((b: EnhancedOrderBox) => b.statusCode === 'WCAC' && !b.isCompleted).length > 0 && (
                            <div className="removable-boxes">
                                {order.boxes
                                    .filter((b: EnhancedOrderBox) => b.statusCode === 'WCAC' && !b.isCompleted)
                                    .map((box: EnhancedOrderBox) => (
                                        <div key={box.id} className="removable-box-item">
                                            <span className="box-label">
                                                {barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)}
                                            </span>
                                            <div className="box-actions">
                                                <button
                                                    className="btn-fail-single-box"
                                                    onClick={() => {
                                                        setBoxToFail(box);
                                                        setShowFailedModal(true);
                                                    }}
                                                    title="Mark as failed"
                                                >
                                                    <FaExclamationTriangle />
                                                </button>
                                                <button
                                                    className="btn-remove-box"
                                                    onClick={() => {
                                                        setBoxToRemove(box);
                                                        setShowRemoveBoxModal(true);
                                                    }}
                                                    title="Remove from order"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        )}
                    </div>
                )}
                {/* Per-box fail controls - delivery orders only */}
                {order.type === 'delivery' && !order.signature && (
                    <div className="box-management-section">
                        {order.boxes.filter((b: EnhancedOrderBox) => b.statusCode === 'ITTC' && !b.isCompleted).length > 0 && (
                            <div className="removable-boxes">
                                {order.boxes
                                    .filter((b: EnhancedOrderBox) => b.statusCode === 'ITTC' && !b.isCompleted)
                                    .map((box: EnhancedOrderBox) => (
                                        <div key={box.id} className="removable-box-item">
                                            <span className="box-label">
                                                {barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)}
                                            </span>
                                            <div className="box-actions">
                                                <button
                                                    className="btn-fail-single-box"
                                                    onClick={() => {
                                                        setBoxToFailDelivery(box);
                                                        setShowFailedDeliveryModal(true);
                                                    }}
                                                    title="Mark as failed delivery"
                                                >
                                                    <FaExclamationTriangle />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                        )}
                    </div>
                )}
                {order.pendingWarehouseBoxes != null && order.pendingWarehouseBoxes > 0 && (
                    <div className="detail-row pending-warehouse-row">
                        <FaWarehouse className="detail-icon warning" />
                        <div className="detail-info">
                            <span className="detail-value warning">{order.pendingWarehouseBoxes} box{order.pendingWarehouseBoxes > 1 ? 'es' : ''} awaiting warehouse preparation</span>
                            <span className="detail-label">These boxes will appear when ready for delivery</span>
                        </div>
                    </div>
                )}
                {addressData && (
                    <div className="address-section">
                        <Address data={addressData} />
                    </div>
                )}
                {/* Revert failed collection boxes (WCFC → WCAC) — initial collection only */}
                {order.type === 'collection' && order.boxes.some((b: EnhancedOrderBox) => b.statusCode === 'WCFC') && (
                    <div className="revert-section">
                        <p className="revert-section-label">Failed boxes (tap to revert):</p>
                        {order.boxes
                            .filter((b: EnhancedOrderBox) => b.statusCode === 'WCFC')
                            .map((box: EnhancedOrderBox) => (
                                <div key={box.id} className="revert-box-item">
                                    <span className="box-label wcfc">{barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} — Failed</span>
                                    <button
                                        className="btn-revert-box"
                                        onClick={() => { setBoxToRevert(box); setRevertTarget('WCAC'); setShowRevertModal(true); }}
                                        title="Revert to Awaiting Collection"
                                    >
                                        <FaUndo /> Revert
                                    </button>
                                </div>
                            ))
                        }
                    </div>
                )}
                {/* Retry failed delivery boxes (ITTKKFR → ITTC) */}
                {order.type?.includes('delivery') && order.boxes.some((b: EnhancedOrderBox) => b.statusCode === 'ITTKKFR') && (
                    <div className="revert-section">
                        <p className="revert-section-label">Failed deliveries (tap to retry):</p>
                        {order.boxes
                            .filter((b: EnhancedOrderBox) => b.statusCode === 'ITTKKFR')
                            .map((box: EnhancedOrderBox) => (
                                <div key={box.id} className="revert-box-item">
                                    <span className="box-label ittkkfr">{barcodeShortLabel(box.barcode, box.id, box.barcodeComponents)} — Failed Return</span>
                                    <button
                                        className="btn-revert-box retry"
                                        onClick={() => { setBoxToRevert(box); setRevertTarget('ITTC'); setShowRevertModal(true); }}
                                        title="Retry delivery"
                                    >
                                        <FaUndoAlt /> Retry
                                    </button>
                                </div>
                            ))
                        }
                    </div>
                )}
                {order.type === 'collection' && !isOrderCompleted && (
                    <div className="failed-collection-section">
                        <button
                            className="btn-failed-collection"
                            onClick={() => setShowFailedModal(true)}
                        >
                            <FaExclamationTriangle />
                            Mark Collection Failed
                        </button>
                    </div>
                )}
                {order.type === 'delivery' && !isOrderCompleted && !isDeliveryBlocked && (
                    <div className="failed-delivery-section">
                        <button
                            className="btn-failed-delivery"
                            onClick={() => setShowFailedDeliveryModal(true)}
                        >
                            <FaExclamationTriangle />
                            Mark Delivery Failed
                        </button>
                    </div>
                )}
            </div>
        );
    }

    const toggleSection = useCallback((sectionName: string) => {
        setOpenSections(prev => {
            const isOpening = !prev[sectionName];
            const newState: Record<string, boolean> = {
                details: false,
                scan: false,
                photo: false,
                signature: false,
                [sectionName]: isOpening
            };

            return newState;
        });
        // Reset manual mode when switching away from scan section
        if (sectionName !== 'scan') {
            setManualMode(false);
        }
    }, []);

    // Action bar handlers
    const handleStartScan = useCallback(async () => {
        setManualMode(false);
        if (activeBoxes.length > 0) {
            await startQuagga({
                boxes: activeBoxes,
                update: updateBox
            });
        }
    }, [activeBoxes, startQuagga, updateBox]);

    // Show graceful "not found" if order doesn't exist in cache (deep link, browser refresh before data loads)
    // No redirect loop — isLoading handles the loading state via useQueryBridge

    if (!order && hasData && !isLoading) {
        return (
            <section>
                <button className="btn-back-to-route" onClick={() => navigate('/courier')}>
                    <FaArrowLeft /> Back to Route
                </button>
                <div className="empty-state">
                    <p>Order not found. It may have been completed or removed from today's route.</p>
                </div>
            </section>
        );
    }

    return (
        <section>
            {order &&
                <>
                    <button className="btn-back-to-route" onClick={() => navigate('/courier')}>
                        <FaArrowLeft /> Back to Route
                    </button>
                    <h1>Order # {order.id}</h1>
                    <h2 className="uppercase">
                        {order.type}
                    </h2>
                    {isDeliveryBlocked && deliveryReadiness?.bannerMessage && (
                        <div className="delivery-blocked-banner">
                            <FaBan className="banner-icon" />
                            <div className="banner-content">
                                <strong>Order Not Ready for Delivery</strong>
                                <p>{deliveryReadiness.bannerMessage}</p>
                                {deliveryReadiness.bannerDetail && (
                                    <p className="banner-detail">{deliveryReadiness.bannerDetail}</p>
                                )}
                            </div>
                        </div>
                    )}
                    <Accordion config={sections.details} isOpen={openSections.details} onToggle={() => toggleSection('details')} />
                    <Accordion config={sections.scan} isOpen={openSections.scan} onToggle={() => toggleSection('scan')} />
                    <Accordion config={sections.photo} isOpen={openSections.photo} onToggle={() => toggleSection('photo')} />
                    <Accordion config={sections.signature} isOpen={openSections.signature} onToggle={() => toggleSection('signature')} />
                </>
            }

            {/* Action Bar - Details: Call customer or office fallback */}
            {openSections.details && (
                <ActionBar>
                    <ActionButton variant="primary" icon={<FaPhone />} onClick={() => window.open('tel:' + (customerPhone || COMPANY_PHONE_TEL))}>
                        {customerPhone ? 'Call Customer' : 'Call Office'}
                    </ActionButton>
                </ActionBar>
            )}

            {/* Action Bar - Scan */}
            {openSections.scan && !isOrderCompleted && (
                <ActionBar>
                    <ActionButton variant="primary" icon={<FaQrcode />} onClick={handleStartScan} disabled={activeBoxes.length === 0}>
                        Scan
                    </ActionButton>
                    <ActionButton variant={manualMode ? 'active' : 'default'} icon={<FaList />} onClick={() => setManualMode(prev => !prev)} disabled={activeBoxes.length === 0}>
                        {manualMode ? 'Exit Manual' : 'Manual'}
                    </ActionButton>
                </ActionBar>
            )}

            {/* Action Bar - Photo */}
            {openSections.photo && proofPhotoBase64 && !isOrderCompleted && (
                <ActionBar>
                    <ActionButton variant="primary" icon={<FaCheck />} onClick={() => toggleSection('signature')}>
                        Continue to Signature
                    </ActionButton>
                </ActionBar>
            )}

            {/* Action Bar - Signature */}
            {openSections.signature && !existingSignatureUrl && !order?.signature
                && allBoxesHandled && !allBoxesFailed
                && (proofPhotoBase64 || existingProofPhotoUrl) && (
                <ActionBar>
                    <ActionButton variant="primary" onClick={handleSignatureSubmit}>
                        Submit Signature
                    </ActionButton>
                </ActionBar>
            )}

            {/* Failed Collection Modal */}
            {showFailedModal && (
                <div className="failed-collection-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowFailedModal(false);
                        setSelectedReason('');
                        setBoxToFail(null);
                        setFailedCollectionPhotoBase64(null);
                    }} />
                    <div className="modal-content">
                        <h3>{boxToFail ? `Mark Box Failed` : `Mark Collection Failed`}</h3>
                        <p>
                            {boxToFail
                                ? `Mark box ${barcodeShortLabel(boxToFail.barcode, boxToFail.id, boxToFail.barcodeComponents)} as failed?`
                                : 'Mark all uncollected boxes as failed?'
                            }
                        </p>
                        <p className="reason-prompt">Select the reason:</p>
                        <div className="reason-list">
                            {FAILED_COLLECTION_REASONS.map((reason) => (
                                <label key={reason.id} className="reason-option">
                                    <input
                                        type="radio"
                                        name="failedReason"
                                        value={reason.id}
                                        checked={selectedReason === reason.id}
                                        onChange={(e) => setSelectedReason(e.target.value)}
                                    />
                                    <span>{reason.label}</span>
                                </label>
                            ))}
                        </div>
                        <div className="modal-photo-section">
                            <p className="reason-prompt"><FaCamera /> Photo proof (optional):</p>
                            {failedCollectionPhotoBase64 ? (
                                <div className="modal-photo-preview">
                                    <img src={failedCollectionPhotoBase64} alt="Proof" />
                                    <button className="btn-retake-modal" onClick={() => setFailedCollectionPhotoBase64(null)}>Retake</button>
                                </div>
                            ) : (
                                <PhotoCapture
                                    onCapture={(base64) => setFailedCollectionPhotoBase64(base64)}
                                    label="Take photo"
                                />
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowFailedModal(false);
                                    setSelectedReason('');
                                    setBoxToFail(null);
                                    setFailedCollectionPhotoBase64(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                disabled={!selectedReason}
                                onClick={() => {
                                    const reasonLabel = FAILED_COLLECTION_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
                                    setShowFailedModal(false);
                                    handleFailedCollection(reasonLabel, boxToFail, failedCollectionPhotoBase64);
                                    setSelectedReason('');
                                    setBoxToFail(null);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Failed Delivery Modal */}
            {showFailedDeliveryModal && (
                <div className="failed-delivery-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowFailedDeliveryModal(false);
                        setSelectedDeliveryReason('');
                        setBoxToFailDelivery(null);
                        setFailedDeliveryPhotoBase64(null);
                    }} />
                    <div className="modal-content">
                        <h3>{boxToFailDelivery ? `Mark Box Failed` : `Mark Delivery Failed`}</h3>
                        <p>
                            {boxToFailDelivery
                                ? `Mark box ${barcodeShortLabel(boxToFailDelivery.barcode, boxToFailDelivery.id, boxToFailDelivery.barcodeComponents)} as failed?`
                                : 'Mark all undelivered boxes as failed?'
                            }
                        </p>
                        <p className="reason-prompt">Select the reason:</p>
                        <div className="reason-list">
                            {FAILED_DELIVERY_REASONS.map((reason) => (
                                <label key={reason.id} className="reason-option">
                                    <input
                                        type="radio"
                                        name="failedDeliveryReason"
                                        value={reason.id}
                                        checked={selectedDeliveryReason === reason.id}
                                        onChange={(e) => setSelectedDeliveryReason(e.target.value)}
                                    />
                                    <span>{reason.label}</span>
                                </label>
                            ))}
                        </div>
                        <div className="modal-photo-section">
                            <p className="reason-prompt"><FaCamera /> Photo proof (optional):</p>
                            {failedDeliveryPhotoBase64 ? (
                                <div className="modal-photo-preview">
                                    <img src={failedDeliveryPhotoBase64} alt="Proof" />
                                    <button className="btn-retake-modal" onClick={() => setFailedDeliveryPhotoBase64(null)}>Retake</button>
                                </div>
                            ) : (
                                <PhotoCapture
                                    onCapture={(base64) => setFailedDeliveryPhotoBase64(base64)}
                                    label="Take photo"
                                />
                            )}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowFailedDeliveryModal(false);
                                    setSelectedDeliveryReason('');
                                    setBoxToFailDelivery(null);
                                    setFailedDeliveryPhotoBase64(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                disabled={!selectedDeliveryReason}
                                onClick={() => {
                                    const reasonLabel = FAILED_DELIVERY_REASONS.find(r => r.id === selectedDeliveryReason)?.label || selectedDeliveryReason;
                                    setShowFailedDeliveryModal(false);
                                    handleFailedDelivery(reasonLabel, boxToFailDelivery, failedDeliveryPhotoBase64);
                                    setSelectedDeliveryReason('');
                                    setBoxToFailDelivery(null);
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Add Box Modal */}
            {showAddBoxModal && (
                <div className="add-box-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowAddBoxModal(false);
                    }} />
                    <div className="modal-content">
                        <h3>Add Box to Order</h3>
                        <p>Add a new box to this collection order.</p>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowAddBoxModal(false);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm-add"
                                onClick={() => {
                                    setShowAddBoxModal(false);
                                    handleAddBox();
                                }}
                            >
                                Add Box
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Box Modal */}
            {showRemoveBoxModal && boxToRemove && (
                <div className="remove-box-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowRemoveBoxModal(false);
                        setBoxToRemove(null);
                        setSelectedRemoveReason('');
                    }} />
                    <div className="modal-content">
                        <h3>Remove Box from Order</h3>
                        <p>Remove box {barcodeShortLabel(boxToRemove.barcode, boxToRemove.id, boxToRemove.barcodeComponents)} from this order?</p>
                        <p className="reason-prompt">Reason (optional):</p>
                        <div className="reason-list">
                            {REMOVE_BOX_REASONS.map((reason) => (
                                <label key={reason.id} className="reason-option">
                                    <input
                                        type="radio"
                                        name="removeBoxReason"
                                        value={reason.id}
                                        checked={selectedRemoveReason === reason.id}
                                        onChange={(e) => setSelectedRemoveReason(e.target.value)}
                                    />
                                    <span>{reason.label}</span>
                                </label>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => {
                                    setShowRemoveBoxModal(false);
                                    setBoxToRemove(null);
                                    setSelectedRemoveReason('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                onClick={() => {
                                    const reasonLabel = REMOVE_BOX_REASONS.find(r => r.id === selectedRemoveReason)?.label;
                                    setShowRemoveBoxModal(false);
                                    handleRemoveBox(boxToRemove, reasonLabel || undefined);
                                    setBoxToRemove(null);
                                    setSelectedRemoveReason('');
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Revert Failed Modal — handles both WCFC→WCAC and ITTKKFR→ITTC */}
            {showRevertModal && boxToRevert && (
                <div className="revert-modal">
                    <div className="modal-backdrop" onClick={() => {
                        setShowRevertModal(false);
                        setBoxToRevert(null);
                    }} />
                    <div className="modal-content">
                        <h3>{revertTarget === 'ITTC' ? 'Retry Delivery' : 'Revert Failed Status'}</h3>
                        <p>
                            {revertTarget === 'ITTC'
                                ? <>Retry delivery for box {barcodeShortLabel(boxToRevert.barcode, boxToRevert.id, boxToRevert.barcodeComponents)}? This will move it from <strong>Failed Return</strong> back to <strong>Out for Delivery</strong>.</>
                                : <>Revert box {barcodeShortLabel(boxToRevert.barcode, boxToRevert.id, boxToRevert.barcodeComponents)} from <strong>Failed Collection</strong> back to <strong>Awaiting Collection</strong>?</>
                            }
                        </p>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => { setShowRevertModal(false); setBoxToRevert(null); }}>
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                onClick={() => handleRevertFailed(boxToRevert, revertTarget)}
                            >
                                {revertTarget === 'ITTC' ? <><FaUndoAlt /> Retry</> : <><FaUndo /> Revert</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default CourierOrder;
